/* PUSSEL-LARM-MODAL — alarm signal that ONLY stops when puzzle is solved.
 * Uses Web Audio (synthesized siren) so we don't depend on any audio file.
 * ADHD-anchored: random puzzle each open, escalating volume on fail. */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlarmClock, X, RefreshCw, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== AUDIO: synthesized siren via Web Audio ==============
function useSiren() {
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);
  const lfoRef = useRef(null);
  const lfoGainRef = useRef(null);
  const playingRef = useRef(false);

  const start = useCallback((volume = 0.18) => {
    if (playingRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      // Carrier
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 660;
      // Master gain
      const gain = ctx.createGain();
      gain.gain.value = 0;
      // LFO to wobble pitch (siren-y)
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 4.2; // wobble Hz
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 220;
      lfo.connect(lfoGain).connect(osc.frequency);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      lfo.start();
      // Ramp up gain so it isn't startling at t=0
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.6);
      oscRef.current = osc;
      gainRef.current = gain;
      lfoRef.current = lfo;
      lfoGainRef.current = lfoGain;
      playingRef.current = true;
    } catch (e) {
      // Silent fallback — visual alarm still works
    }
  }, []);

  const setVolume = useCallback((v) => {
    try {
      if (!gainRef.current || !ctxRef.current) return;
      const ctx = ctxRef.current;
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(Math.min(0.55, Math.max(0, v)), ctx.currentTime + 0.3);
    } catch {/**/}
  }, []);

  const stop = useCallback(() => {
    try {
      if (!playingRef.current) return;
      const ctx = ctxRef.current;
      if (gainRef.current && ctx) {
        gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
        gainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
      }
      setTimeout(() => {
        try { oscRef.current && oscRef.current.stop(); } catch {/**/}
        try { lfoRef.current && lfoRef.current.stop(); } catch {/**/}
        try { ctxRef.current && ctxRef.current.close(); } catch {/**/}
        oscRef.current = null;
        lfoRef.current = null;
        gainRef.current = null;
        lfoGainRef.current = null;
        ctxRef.current = null;
        playingRef.current = false;
      }, 500);
    } catch {/**/}
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { start, stop, setVolume };
}

// ============== PUZZLE GENERATORS ==============
function genMath() {
  const a = 11 + Math.floor(Math.random() * 79);
  const b = 11 + Math.floor(Math.random() * 79);
  const op = Math.random() < 0.5 ? '+' : '−';
  const answer = op === '+' ? a + b : Math.abs(a - b);
  const question = `${op === '+' ? a : Math.max(a, b)} ${op} ${op === '+' ? b : Math.min(a, b)}`;
  return { type: 'math', prompt: question, expected: String(answer) };
}

function genDigits() {
  let s = '';
  for (let i = 0; i < 5; i++) s += Math.floor(Math.random() * 10);
  return { type: 'digits', prompt: s, expected: s, showTimeMs: 2400 };
}

const WAKE_SENTENCES = [
  'Jag är vaken och redo för dagen',
  'En sak i taget och jag klarar det',
  'Mina fötter är på golvet nu',
  'Jag dricker vatten innan jag rör mobilen',
  'Idag tar jag hand om mig själv',
];
function genSentence() {
  const s = WAKE_SENTENCES[Math.floor(Math.random() * WAKE_SENTENCES.length)];
  return { type: 'sentence', prompt: s, expected: s };
}

function pickPuzzle(kind = 'random') {
  if (kind === 'math') return genMath();
  if (kind === 'digits') return genDigits();
  if (kind === 'sentence') return genSentence();
  const gens = [genMath, genDigits, genSentence];
  return gens[Math.floor(Math.random() * gens.length)]();
}

function normalize(s) {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.,!?]+$/g, '');
}

// ============== MODAL ==============
export default function PuzzleAlarmModal({ alarm, onDismissed, onCancel }) {
  const siren = useSiren();
  const [puzzle, setPuzzle] = useState(() => pickPuzzle(alarm?.puzzle_type || 'random'));
  const [input, setInput] = useState('');
  const [showDigits, setShowDigits] = useState(true);
  const [fails, setFails] = useState(0);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  // Start siren as soon as modal opens (volume starts low to be kind)
  useEffect(() => {
    siren.start(0.18);
    return () => siren.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For "digits" puzzle: show for showTimeMs, then hide
  useEffect(() => {
    if (puzzle.type !== 'digits') { setShowDigits(true); return; }
    setShowDigits(true);
    const t = setTimeout(() => setShowDigits(false), puzzle.showTimeMs || 2400);
    return () => clearTimeout(t);
  }, [puzzle]);

  useEffect(() => {
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [puzzle]);

  const escalateVolume = useMemo(() => {
    // 0.18 → 0.30 → 0.40 → 0.50
    return Math.min(0.55, 0.18 + fails * 0.10);
  }, [fails]);

  useEffect(() => { siren.setVolume(escalateVolume); }, [escalateVolume, siren]);

  const submit = useCallback(async () => {
    const ok = normalize(input) === normalize(puzzle.expected);
    if (!ok) {
      setFails((f) => f + 1);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error('Fel — pusslet byts ut och larmet höjs 🚨');
      try { if (alarm?.id) await axios.post(`${API}/puzzle-alarms/${alarm.id}/fail`, {}, { withCredentials: true }); } catch {/**/}
      setPuzzle(pickPuzzle(alarm?.puzzle_type || 'random'));
      return;
    }
    // SUCCESS
    siren.stop();
    try { if (alarm?.id) await axios.post(`${API}/puzzle-alarms/${alarm.id}/complete`, {}, { withCredentials: true }); } catch {/**/}
    toast.success('🌅 Bra jobbat — larmet är av!');
    onDismissed?.();
  }, [input, puzzle, alarm, siren, onDismissed]);

  const reshuffle = () => setPuzzle(pickPuzzle(alarm?.puzzle_type || 'random'));

  const label = alarm?.label || 'Pussel-larm';
  const onKeyDown = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="puzzle-alarm-modal">
      <div className={`w-full max-w-md bg-white rounded-3xl shadow-2xl border-4 border-rose-500 overflow-hidden ${shake ? 'animate-[shake_0.5s]' : ''}`}>
        <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-12px)} 75%{transform:translateX(12px)} }`}</style>
        {/* Pulsing header */}
        <div className="bg-gradient-to-br from-rose-500 via-pink-500 to-orange-500 p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-white/20 animate-pulse" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlarmClock className="w-8 h-8 animate-bounce" />
              <div>
                <div className="text-xs uppercase tracking-wider opacity-90">LARMET RINGER</div>
                <div className="text-xl font-extrabold drop-shadow" data-testid="alarm-label">{label}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase opacity-80">Försök</div>
              <div className="text-2xl font-extrabold tabular-nums" data-testid="fail-count">{fails}</div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500 italic text-center">
            ✨ Lös pusslet för att stänga av — fuska = nytt pussel + högre ljud
          </p>

          {/* PUZZLE CARD */}
          {puzzle.type === 'math' && (
            <div className="bg-gradient-to-br from-violet-100 to-pink-100 rounded-2xl p-6 text-center" data-testid="puzzle-math">
              <div className="text-xs uppercase text-violet-700 mb-1">Mattepussel</div>
              <div className="text-5xl font-extrabold text-violet-900 tabular-nums tracking-wider">
                {puzzle.prompt} = ?
              </div>
            </div>
          )}

          {puzzle.type === 'digits' && (
            <div className="bg-gradient-to-br from-sky-100 to-teal-100 rounded-2xl p-6 text-center min-h-[140px] flex flex-col items-center justify-center" data-testid="puzzle-digits">
              <div className="text-xs uppercase text-sky-700 mb-1">Memorera siffrorna</div>
              {showDigits ? (
                <div className="text-5xl font-extrabold text-sky-900 tabular-nums tracking-[0.4em]" data-testid="digits-shown">
                  {puzzle.prompt}
                </div>
              ) : (
                <div className="text-base text-sky-800 font-medium">Skriv siffrorna du nyss såg ⬇️</div>
              )}
            </div>
          )}

          {puzzle.type === 'sentence' && (
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-6 text-center" data-testid="puzzle-sentence">
              <div className="text-xs uppercase text-amber-700 mb-2">Skriv av exakt</div>
              <div className="text-lg sm:text-xl font-bold text-amber-900 italic leading-snug">
                "{puzzle.prompt}"
              </div>
            </div>
          )}

          {/* INPUT */}
          <input
            ref={inputRef}
            type={puzzle.type === 'sentence' ? 'text' : 'text'}
            inputMode={puzzle.type === 'sentence' ? 'text' : 'numeric'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={puzzle.type === 'sentence' ? 'Skriv meningen…' : 'Ditt svar…'}
            className="w-full px-4 py-4 rounded-2xl border-4 border-rose-300 focus:border-rose-500 outline-none text-xl font-bold text-center tabular-nums"
            data-testid="alarm-input"
            autoFocus
          />

          <div className="flex gap-2">
            <button
              onClick={submit}
              data-testid="alarm-submit"
              className="flex-1 px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-extrabold text-base shadow-lg flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> Stäng av larmet
            </button>
            <button
              onClick={reshuffle}
              data-testid="alarm-reshuffle"
              title="Byt pussel"
              className="px-3 py-3 rounded-2xl bg-white border-2 border-rose-200 hover:bg-rose-50 text-rose-600"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Kindness escape — only after many fails (mjuk hand) */}
          {fails >= 4 && onCancel && (
            <button
              onClick={() => { siren.stop(); onCancel(); }}
              data-testid="alarm-cancel"
              className="w-full text-xs text-gray-400 hover:text-gray-600 underline italic flex items-center justify-center gap-1 mt-2"
            >
              <X className="w-3 h-3" /> Jag mår inte bra — låt mig stänga ändå
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
